/**
 * SherlockTab — superadmin-only daily activity ledger (rendered only when
 * user.role === "superadmin"; the API 404s for everyone else).
 * Per user per day: clocked-in hours (from the punch clock), active app time
 * (silent interaction beats), and the missing difference in red. On today's
 * date the Active column ticks live next to a pulsing dot while someone is
 * active. Never referenced anywhere else in the app; no notifications.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmt = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};
const fmtTick = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const todayISO = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
};
const shiftISO = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const pretty = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

const TH = "px-3 py-2 text-left font-mono text-[9px] uppercase tracking-widest text-white/50";
const TD = "px-3 py-2.5 text-xs text-white/85";

export default function SherlockTab() {
  const [date, setDate] = useState(todayISO());
  const [mode, setMode] = useState("day"); // "day" | "week"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0); // 1s re-render for live counters
  const pollRef = useRef(null);

  const load = useCallback(async (d, m, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/sherlock", { params: { date: d, mode: m } });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date, mode); }, [date, mode, load]);

  // resync today's live rows from the server every 30s
  useEffect(() => {
    clearInterval(pollRef.current);
    if (data && mode === "day" && date === data.today) {
      pollRef.current = setInterval(() => load(date, mode, true), 30000);
    }
    return () => clearInterval(pollRef.current);
  }, [date, mode, data?.today, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // 1s local ticker — only matters when viewing today with live rows
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const isToday = data && mode === "day" && date === data.today;
  const rows = data?.rows || [];
  const step = mode === "week" ? 7 : 1;
  const atLatest = data && (mode === "week" ? data.week_end >= data.today : date >= data.today);
  const rangeLabel = mode === "week" && data
    ? `${pretty(data.week_start)} – ${pretty(data.week_end)}`
    : pretty(date);

  const displayActive = (r) => {
    // server seconds cover everything up to the last beat; for live rows add
    // the ongoing stretch since that beat so the counter ticks in real time
    if (isToday && r.live && r.last_beat) {
      return r.active_seconds + Math.max(0, (Date.now() - new Date(r.last_beat).getTime()) / 1000);
    }
    return r.active_seconds;
  };

  return (
    <div className="space-y-4" data-testid="sherlock-tab">
      {/* mode + date navigation — same arrows as the Hours page */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-white/15" data-testid="sherlock-mode-toggle">
          {[["day", "Day"], ["week", "Week"]].map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              data-testid={`sherlock-mode-${m}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                mode === m ? "bg-[#1a3fd1] text-white" : "bg-white/5 text-white/55 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setDate((d) => shiftISO(d, -step))}
          data-testid="sherlock-prev-day"
          aria-label={mode === "week" ? "Previous week" : "Previous day"}
          className="rounded-md border border-white/15 bg-white/5 p-2 text-white/70 hover:border-white/25 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <input
          type="date"
          value={date}
          max={todayISO()}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          data-testid="sherlock-date"
          className="rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none"
        />
        <button
          onClick={() => setDate((d) => shiftISO(d, step))}
          disabled={!!atLatest}
          data-testid="sherlock-next-day"
          aria-label={mode === "week" ? "Next week" : "Next day"}
          className="rounded-md border border-white/15 bg-white/5 p-2 text-white/70 hover:border-white/25 hover:text-white disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="ml-1 text-sm font-semibold text-white/80" data-testid="sherlock-date-label">{rangeLabel}</span>
        {isToday && <span className="rounded bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-emerald-400">Live</span>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="sherlock-table">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="bg-white/5">
              <th className={TH}>Name</th>
              <th className={`${TH} text-right`}>Clocked in</th>
              <th className={`${TH} text-right`}>Active in app</th>
              <th className={`${TH} text-right`}>Missing</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={4} className="px-3 py-10 text-center text-xs text-white/40">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              </td></tr>
            )}
            {data && rows.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-10 text-center text-xs text-white/40">Nobody on record for this day.</td></tr>
            )}
            {rows.map((r) => {
              const active = displayActive(r);
              const missing = Math.max(0, r.clocked_seconds - active);
              return (
                <tr key={r.user_id} className="border-t border-white/5" data-testid={`sherlock-row-${r.user_id}`}>
                  <td className={TD}>
                    <div className="flex items-center gap-2">
                      {isToday && r.live && (
                        <span className="relative flex h-2 w-2" data-testid={`sherlock-live-${r.user_id}`}>
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                        </span>
                      )}
                      <span className="font-medium text-white/90">{r.name}</span>
                      <span className="font-mono text-[9px] uppercase tracking-wider text-white/30">{r.role}</span>
                    </div>
                  </td>
                  <td className={`${TD} text-right tabular-nums`} data-testid={`sherlock-clocked-${r.user_id}`}>{fmt(r.clocked_seconds)}</td>
                  <td className={`${TD} text-right tabular-nums`} data-testid={`sherlock-active-${r.user_id}`}>
                    {isToday && r.live ? (
                      <span className="font-mono text-emerald-300">{fmtTick(active)}</span>
                    ) : fmt(active)}
                  </td>
                  <td className={`${TD} text-right font-semibold tabular-nums ${missing > 60 ? "text-red-400" : "text-white/40"}`}
                    data-testid={`sherlock-missing-${r.user_id}`}>
                    {missing > 60 ? fmt(missing) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-white/25">Active time counts app interaction and stops after 5 quiet minutes.</p>
    </div>
  );
}
